import { flowStatus, type GuideHealth, guideHealth, type GuideRow } from '@/app/guide/flowStatus';
import { draftFlowsState } from '@/state/draftFlows.state';
import { currentFlowState } from '@/state/flow.state';
import type { AppFlowObjectKnown } from '@/util/AppFlowObjectKnown';
import { AppFlowDao, GuideDao } from '@rock/db-admin';
import type { AppFlowParams, GuideContext } from '@rock/types';
import { shallowEqual } from '@rock/util';
import { atom, type Atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';

const appFlowDao = new AppFlowDao();
const guideDao = new GuideDao();

/**
 * Whether a guide is staged in preview but not yet live (drives the "In Preview" badge and gates
 * Publish to Live in the editor). True when a preview exists that differs from the live guide.
 */
export const guideStagePendingState = atomFamily(
    (context: GuideContext) => guideDao.guideStagePendingAtom(context),
    shallowEqual,
);

/** The GuideContext a set of flow params addresses (equipment-type guides vs. space guides). */
export function guideContextForParams(params: AppFlowParams): GuideContext {
    return params.equipmentTypeId
        ? { type: 'equipment', typeId: params.equipmentTypeId }
        : { type: 'space', spaceId: params.spaceId };
}

/**
 * The `flowId` of the flow currently staged in preview for a context — but only while that preview is
 * genuinely pending (nothing live yet, or the preview differs from live). Undefined otherwise. Drives
 * the "preview" badge and the auto-select-on-load fallback when no flow is published.
 */
export const previewGuideFlowIdState = atomFamily((context: GuideContext) => {
    const previewAtom = guideDao.guidePreviewAtom(context);
    return atom(async (get): Promise<string | undefined> => {
        const pending = await get(guideStagePendingState(context));
        if (!pending) return undefined;
        return (await get(previewAtom))?.flowId;
    });
}, shallowEqual);

export const liveGuideFlowState = atomFamily(
    // Read-only, narrowed for the same reason as `currentFlowState` — see there.
    (params: AppFlowParams) => appFlowDao.liveFlowAtom(params) as Atom<Promise<AppFlowObjectKnown | undefined>>,
    shallowEqual,
);

/** Where the flow open in the editor stands — the header badges. */
export const currentFlowStatusState = atomFamily(
    (params: AppFlowParams) =>
        loadable(
            atom(async (get) => {
                const [liveFlow, previewFlowId, currentFlow] = await Promise.all([
                    get(liveGuideFlowState(params)),
                    get(previewGuideFlowIdState(guideContextForParams(params))),
                    get(currentFlowState(params)),
                ]);
                return flowStatus(currentFlow, liveFlow, previewFlowId);
            }),
        ),
    shallowEqual,
);

/**
 * Every draft guide for a space or equipment type, each with its status — the Guides table.
 *
 * The two things a status is judged against (the live guide, the staged preview) are per-context,
 * not per-guide, so this is three reads no matter how many guides there are; the rest is comparison
 * over data the list already carries.
 */
export const guideRowsState = atomFamily(
    (params: AppFlowParams) =>
        atom(async (get): Promise<GuideRow[]> => {
            const [flows, liveFlow, previewFlowId] = await Promise.all([
                get(draftFlowsState(params)),
                get(liveGuideFlowState(params)),
                get(previewGuideFlowIdState(guideContextForParams(params))),
            ]);
            return flows.map((flow) => ({ flow, status: flowStatus(flow, liveFlow, previewFlowId) }));
        }),
    shallowEqual,
);

/**
 * The status dot: is a guide live, and is what's live current?
 *
 * Deliberately leaner than `guideRowsState` — the drafts come from the one per-space subscription
 * the list already holds, so a dot costs a single extra document (the live guide). That's what makes
 * it affordable per row of the equipment-type catalog.
 */
export const guideHealthState = atomFamily(
    (params: AppFlowParams) =>
        atom(async (get): Promise<GuideHealth> => {
            const [flows, liveFlow] = await Promise.all([
                get(draftFlowsState(params)),
                get(liveGuideFlowState(params)),
            ]);
            return guideHealth(flows, liveFlow);
        }),
    shallowEqual,
);
